<template>
	<dialog ref="dialog" @close="emit('update:open', false)" @keyup.enter="() => handleAdd(selectedItems)">
		<form @submit.prevent>
			<input
				type="text"
				autofocus
				autocomplete="off"
				:placeholder="placeholder"
				@input="({ target }) => onInput((target as HTMLInputElement)?.value)"
			/>
			<Button type="button" variant="primary" size="xs" class="add" @click="() => handleAdd(selectedItems)">
				{{ $t("search-modal.add") }}
			</Button>
			<Button type="button" variant="secondary" size="xs" class="close" @click="emit('update:open', false)">
				{{ $t("search-modal.close") }}
			</Button>
		</form>

		<span :class="{ invisible: !showErrorMessage }" class="error-message">
			{{ $t("search-modal.selectAtLeastOne", { entity: entityLabel ?? "" }) }}
		</span>

		<ul class="results">
			<li v-for="item in results" :key="item[idKey]" :data-id="item[idKey]" class="result">
				<label :for="item[idKey]" class="details">
					<slot name="details" :item="item"></slot>
				</label>
				<input type="checkbox" :id="item[idKey]" v-model="selectedItems" :value="item" />
			</li>
		</ul>

		<Button
			v-if="createUrl && hasSearched && results.length === 0"
			:href="createUrl"
			target="_blank"
			size="s"
			variant="external"
			class="create-link"
		>
			{{ $t("search-modal.create-in-hubspot") }}
		</Button>
	</dialog>
</template>

<script setup lang="ts">
import { ref, watch, toRaw } from "vue"
import Button from "@/components/ui/Button.vue"

const props = defineProps<{
	results: Array<any>
	open: boolean
	placeholder?: string
	entityLabel?: string
	idKey: string
	createUrl?: string
}>()

const emit = defineEmits(["update:open", "pattern", "add"])

const selectedItems = ref([])
const showErrorMessage = ref(false)
const hasSearched = ref(false)
const dialog = ref<HTMLDialogElement | null>(null)

watch(
	() => props.open,
	open => {
		if (open) {
			dialog.value?.showModal()
		} else {
			selectedItems.value = []
			showErrorMessage.value = false
			hasSearched.value = false
			dialog.value?.close()
		}
	}
)

function onInput(value: string) {
	hasSearched.value = value.length > 0
	emit("pattern", value)
}

function handleAdd(items = []) {
	if (items.length === 0) {
		showErrorMessage.value = true
	} else {
		emit("add", items.map(toRaw))
		emit("update:open", false)
	}
}

// Erase error message after 3 seconds
watch(showErrorMessage, show => {
	if (!show) {
		return
	}
	window.setTimeout(() => {
		showErrorMessage.value = !show
	}, 3_000)
})
</script>

<style scoped>
dialog {
	&:not([open]) {
		display: none;
	}

	position: absolute;
	left: 50%;
	top: 50%;
	transform: translate(-50%, -50%);
	width: var(--width-search-modal);
	height: 30vh;
	display: flex;
	flex-direction: column;
	gap: var(--spacing-xs);
	font-family: var(--font-family-primary);
	font-size: var(--font-size-small);
	padding: var(--spacing-md) var(--spacing-lg);
	border: none;
	border-radius: var(--border-radius-lg);
	background-color: var(--neutral-0);
	box-shadow: var(--shadow-xl);
	scrollbar-gutter: stable;

	form {
		display: flex;
		justify-content: space-between;
		gap: var(--spacing-sm);
		font-size: var(--font-size-small);

		input {
			flex: 1;
			padding: var(--spacing-xs);
			font-family: var(--font-family-primary);
			font-size: var(--font-size-small);
			border: var(--border-width-thin) solid var(--neutral-400);
			border-radius: var(--border-radius-base);

			&:focus {
				outline: none;
				border-color: var(--primary-green);
			}
		}

		.add,
		.close {
			flex-shrink: 0;
		}
	}

	.error-message {
		display: flex;
		min-height: var(--min-height-element);
		justify-content: center;
		font-family: var(--font-family-primary);
		font-size: var(--font-size-xs);
		font-weight: var(--font-weight-semibold);
		color: var(--accent-warning);
	}

	.results {
		list-style: none;
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xxs);
		overflow-y: auto;

		.result {
			display: flex;
			justify-content: space-between;
			align-items: center;
			padding: var(--spacing-xxs) var(--spacing-xs);
			border-radius: var(--border-radius-sm);
			transition: background-color 0.2s ease;

			&:nth-child(odd) {
				background-color: var(--neutral-100);
			}

			&:hover {
				background-color: var(--brand-light-green);
			}

			.details {
				cursor: pointer;
				font-family: var(--font-family-primary);
				font-size: var(--font-size-small);
				flex: 1;
			}

			input[type="checkbox"] {
				transform: scale(1.1);
				margin-right: var(--spacing-sm);
				outline: none;
				cursor: pointer;
			}
		}
	}

	.create-link {
		align-self: center;
	}
}
</style>
